import { Controller } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Field, FieldError } from '@/shared/components/ui/field';
import { DateOnlyPicker } from './DateOnlyPicker';
import { TimeSelect } from '@/shared/components/ui/TimeSelect';
import { useUIStore } from '@/core/stores/ui.store';
import { instantToZonedParts, zonedToInstant } from '@/shared/lib/datetime/instant';

interface FormFieldDateTimePickerProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * RHF-integrated date + time picker for a UTC ISO `Instant`. Composed from the
 * prefs-aware `DateOnlyPicker` (honors `dateFormat`) and `TimeSelect` (honors
 * `timeFormat`), and it reads/writes the instant in the user's configured
 * `timezone` (via `instantToZonedParts` / `zonedToInstant`) — NOT the browser
 * offset. Use it wherever a form needs a precise date-time.
 *
 * @example
 * <FormFieldDateTimePicker control={control} name="startTime" label={t('start')} />
 */
export function FormFieldDateTimePicker<T extends FieldValues>({
  control,
  name,
  label,
  disabled,
  'data-testid': testId,
}: FormFieldDateTimePickerProps<T>) {
  const timeFormat = useUIStore((s) => s.timeFormat);
  const timezone = useUIStore((s) => s.timezone);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const parts = instantToZonedParts(field.value, timezone);
        const datePart = parts?.date ?? '';
        const timePart = parts?.time ?? '';

        const emit = (date: string, time: string) => {
          if (!date) {
            field.onChange(null);
            return;
          }
          field.onChange(zonedToInstant(date, time || '00:00', timezone));
        };

        return (
          <Field data-invalid={fieldState.invalid}>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <DateOnlyPicker
                id={name}
                label={label}
                value={datePart || null}
                onChange={(date) => emit(date ?? '', timePart || '09:00')}
                invalid={fieldState.invalid}
                disabled={disabled}
                className="sm:flex-1"
                data-testid={testId}
              />
              <TimeSelect
                value={timePart || null}
                timeFormat={timeFormat}
                disabled={disabled || !datePart}
                onChange={(time) => emit(datePart, time)}
                testId={testId ? `${testId}-time` : undefined}
              />
            </div>
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
          </Field>
        );
      }}
    />
  );
}
